import { BaseModel } from '../base.model';

/**
 * Change signals for feature modules, as opposed to supporting-table imports.
 *
 * Same mechanism and same document shape as the supporting-table signals — a
 * counter a client subscribes to, not the data itself — but a separate collection
 * because `SupportingTableId` is a closed union describing imported reference
 * tables. A bulletin session is not one of those, and widening that type to make
 * it fit would make the name a lie.
 *
 * The client re-reads from the API when `version` moves; nothing here is
 * authoritative, which is what keeps API permission checks the only gate on the
 * underlying data.
 */
/**
 * @deprecated Superseded by `signals/domain-signal.model`, which carries a domain in
 * the path so non-reporting subsystems can emit signals without pretending to be
 * reporting modules. Kept because the publisher still mirrors `leaders-bulletins`
 * here for one release, so a converter deployed ahead of the clients does not leave
 * an old client watching a document that stopped moving. Remove both the mirror and
 * this file once every client reads the domain path.
 */
export const REPORTING_MODULE_SIGNALS_COLLECTION = 'modules';

export enum ModuleSignalId {
  leadersBulletins = 'leaders-bulletins',
}

export const MODULE_SIGNAL_IDS: ModuleSignalId[] = Object.values(ModuleSignalId);

const MODULE_SIGNAL_ID_SET = new Set<string>(MODULE_SIGNAL_IDS);

export function isModuleSignalId(value: string): value is ModuleSignalId {
  return MODULE_SIGNAL_ID_SET.has(value);
}

export enum ModuleSignalKeys {
  moduleId = 'moduleId',
  version = 'version',
  updatedAt = 'updatedAt',
  lastOperation = 'lastOperation',
  lastOutboxEventId = 'lastOutboxEventId',
  source = 'source',
}

export interface ModuleSignal extends BaseModel {
  [ModuleSignalKeys.moduleId]: ModuleSignalId;
  /** Monotonic. Compare it; the value itself carries no meaning. */
  [ModuleSignalKeys.version]: number;
  [ModuleSignalKeys.updatedAt]: unknown;
  [ModuleSignalKeys.lastOperation]: string;
  [ModuleSignalKeys.lastOutboxEventId]: number;
  [ModuleSignalKeys.source]: string;
}
